import { useEffect } from "react";

/**
 * Scope guard for the Companion PWA (Bonus Hardening).
 *
 * When the Companion is launched as an installed PWA (display-mode: standalone
 * AND current path is under `/companion`), we don't want the user to be able
 * to drift into the main marketing site / patient dashboard from the
 * fullscreen app — that would defeat the whole point of a separate PWA.
 *
 * This listener intercepts any top-window navigation attempt to a URL
 * outside `/companion/*` and re-routes it to the system browser via
 * `window.open(url, '_blank')`. Internal Companion links keep working as
 * normal (React Router handles them before the browser sees a full page
 * navigation, so the listener never fires).
 *
 * Running in a regular browser tab (not standalone), the guard is a no-op.
 */
export default function CompanionScopeGuard() {
  useEffect(() => {
    const isStandalone =
      (typeof window !== "undefined" &&
        (window.matchMedia?.("(display-mode: standalone)").matches ||
          window.navigator?.standalone)) ||
      false;
    if (!isStandalone) return undefined;

    const onClick = (e) => {
      // Defensive: only act when the current URL is actually inside the
      // Companion scope. The component is only meant to be mounted on
      // /companion routes, but if it ever leaks elsewhere (HMR glitch,
      // an installed-PWA window that crossed routes without a proper
      // unmount, etc.) we MUST NOT intercept clicks on the main site —
      // doing so silently breaks every footer / nav link because the
      // popup blocker swallows our `window.open(_blank)` fallback,
      // exactly matching the "left-click does nothing, right-click →
      // open in new tab works" symptom.
      if (typeof window === "undefined" ||
          window.location.pathname.indexOf("/companion") !== 0) {
        return;
      }

      // Walk up to find the nearest <a> — handles nested spans inside links.
      const a = e.target?.closest?.("a");
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href) return;
      // Ignore hash-only, mailto:, tel:, sms:, javascript: — those are fine
      // inside the PWA and should open with their native handler.
      if (
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("sms:") ||
        href.startsWith("javascript:")
      ) {
        return;
      }

      let url;
      try {
        url = new URL(href, window.location.href);
      } catch (_) {
        return;
      }
      // Same-origin links inside /companion/* are allowed. Anything else
      // (different host OR different path prefix) pops out to the system
      // browser so the PWA window stays locked to Companion.
      const sameOrigin = url.origin === window.location.origin;
      const insideCompanion = url.pathname.indexOf("/companion") === 0;
      if (sameOrigin && insideCompanion) return;

      e.preventDefault();
      e.stopPropagation();
      try {
        window.open(url.toString(), "_blank", "noopener,noreferrer");
      } catch (_) { /* swallow */ }
    };

    // Capture phase so we intercept before React Router's own handlers.
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  return null;
}
